/* tslint:disable:variable-name */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  SkipSelf,
  ViewChild,
} from '@angular/core';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {BrandDesign, RichText, Token} from '@paperlessio/sdk/api/models';
import {TokenScrollService} from '@management/editor/tokens/token-scroll.service';
import Quill, {RangeStatic} from 'quill';
import {instances, VariableBlot} from './variable.blot';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {DOCUMENT} from '@angular/common';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {combineLatest, fromEvent, Subscription} from 'rxjs';
import {DropdownComponent} from '@paperlessio/sdk/components/dropdown';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {EditorRoutingService} from '@management/editor/editor-routing.service';
import {DropdownItem} from '@blocks/rich-text/dropdown-item';
import {TokenStore} from '@management/editor/tokens/token.store';
import {BlockStore} from '@blocks/block/block.store';
import {EDITOR_SIZES} from '@blocks/rich-text/rich-text.consts';
import Parchment from 'parchment';
import {RichTextToolbarComponent, RichTextToolbarState} from '@blocks/rich-text/rich-text-toolbar/rich-text-toolbar.component';
import {DraggableService} from '@blocks/draggable/draggable.service';
import {TableComponent} from '@blocks/table/table.component';
import {RichTextService} from '@blocks/rich-text/rich-text.service';
import {BlockComponent} from '@blocks/block/block.component';
import {RouterStore} from '@paperlessio/sdk/util';
import {LocalizationService} from '@shared/i18n/localization.service';
import {ModalComponent} from '@paperlessio/sdk/modal';
import {CalculatedTokenValueStore} from '@shared/rex/calculated-token-value.store';
import {TokenHelper} from '@paperlessio/sdk/util/helpers';
import {BrandDesignStore} from '@paperlessio/sdk/api/stores';
import {ContentLocale} from '@paperlessio/sdk/api/util';

const Delta = Quill.import('delta');
const SizeClass = Quill.import('attributors/class/size');

SizeClass.whitelist = EDITOR_SIZES.map(size => size.value);

Quill.register(SizeClass, true);
Quill.register(VariableBlot);

@Component({
  selector: 'app-rich-text',
  templateUrl: './rich-text.component.html',
  styleUrls: ['./rich-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RichTextComponent implements OnInit, AfterViewInit, OnDestroy {
  static instaceMap = new Map<number, RichTextComponent>();

  constructor(
    @Optional() public tokenStore: TokenStore,
    @Optional() public calculatedTokenValueStore: CalculatedTokenValueStore,
    @Optional() public tokenScrollService: TokenScrollService,
    @Optional() public blockStore: BlockStore,
    @Optional() public blockChangeDetectionService: BlockChangeDetectionService,
    @Optional() @SkipSelf() private blockComponent: BlockComponent,
    @Optional() @Inject(BLOCKS_CONFIG) public blocksConfig: BlocksConfig,
    @Optional() private editorRoutingService: EditorRoutingService,
    @Optional() private blockMutationService: BlockMutationService,
    @Optional() private draggableService: DraggableService,
    @Optional() private routerStore: RouterStore,
    @Optional() private richTextService: RichTextService,
    @Optional() private modalComponent: ModalComponent,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private zone: NgZone,
    @Inject(DOCUMENT) private document: Document,
    private designStore: BrandDesignStore) {
  }

  private tableComponent = inject(TableComponent, {optional: true});
  private _typing: boolean;
  private preventUpdate = false;
  private subs = new Subscription();
  private destroyed = false;
  private showCount = 0;

  @Input() block: RichText;
  @Input() externalToolbarTarget: HTMLElement;
  @Input() toolbarScrollingContainer: HTMLDivElement;

  @Output() focused = new EventEmitter<void>();
  @Output() blurred = new EventEmitter<void>();
  @Output() textSelected = new EventEmitter<string>();
  @Output() stateChange = new EventEmitter<RichTextToolbarState>();

  @ViewChild('editorDiv') editor: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarDropdown') private toolbarDropdown: DropdownComponent;
  @ViewChild(RichTextToolbarComponent) richTextToolbarComponent: RichTextToolbarComponent;

  private localizationService = inject(LocalizationService);

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  get tableMode(): boolean {
    return !!this.tableComponent;
  }

  get typing(): boolean {
    return this._typing;
  }

  set typing(value: boolean) {
    if (this._typing !== value && this.toolbarDropdown != null) {
      if (!!value) {
        this.toolbarDropdown.hide();
      } else {
        this.toolbarDropdown.show();
      }

      this.cdr.markForCheck();
    }

    this._typing = value;
  }

  quill: Quill;

  private _state: RichTextToolbarState = new RichTextToolbarState({});
  get state(): RichTextToolbarState {
    return this._state;
  }

  set state(v: RichTextToolbarState) {
    this._state = v;
    this.stateChange.emit(v);
  }

  selectedText = '';
  variables: Token[] = [];
  variablesMapped: { name: string, variable: Token }[] = [];
  variableSearchQuery = '';
  currentSelection: RangeStatic;
  previousSelection: RangeStatic;

  ngOnInit() {
    this.showCount = 0;
    this.draggableService?.isDragging$
      ?.pipe(distinctUntilChanged())
      ?.subscribe(v => {
        if (!!v && !!this.toolbarDropdown) {
          this._typing = true;
          this.toolbarDropdown.hide();
        }
      });

    if (!this.toolbarScrollingContainer) {
      this.toolbarScrollingContainer = this.document.querySelector<HTMLDivElement>('.bucket-wrapper');
    }

    this.subs.add(this.designStore.design
      .pipe(filter((x: BrandDesign) => !!x?.brand_styles))
      .subscribe((design: BrandDesign) => {
        this.state.presets = design.brand_styles?.colors?.map(color => {
          return 'var(--design-' + color.name + ')';
        });
      }));

    this.subs.add(this.tokenStore.tokens
      .pipe(filter(v => !!v))
      .subscribe(tokens => {
        this.variables = tokens.sort((x, y) => x.name.localeCompare(y.name));
        this.cdr.markForCheck();
      }));

    if (this.editorRoutingService) {
      this.subs.add(this.editorRoutingService.changes.subscribe(() => {
        if (this.blocksConfig?.editable && this.block?.id === this.editorRoutingService?.selectedBlock) {
          // Check if the this.scrollingContainer is still existing in dom. If not, set it new.
          if (this.showCount <= 0) {
            this.toolbarScrollingContainer = this.document.querySelector<HTMLDivElement>('.bucket-wrapper');

            // We need to detect changes because we NEED the changes to be detected in the next code line.
            this.cdr.detectChanges();
          }

          this.showCount += 1;
          this.toolbarDropdown?.show();
        } else {
          this.toolbarDropdown?.hide();
        }
      }));
    }

    if (this.blockChangeDetectionService) {
      this.subs.add(this.blockChangeDetectionService.changedBlockIds$
        .pipe(filter(id => id === this.block.id && this.quill?.root?.style != null))
        .subscribe(id => {
          this.quill.root.style.justifyContent = this.block.settings.verticalAlignment;
        }));
    }

    if (this.blocksConfig.editable && this.richTextService) {
      this.subs.add(this.richTextService.focus$
        .pipe(filter(id => this.block.id === id))
        .subscribe(id => {
          this.quill.root.focus();
        }));

      this.subs.add(this.richTextService.blur$
        .subscribe(() => {
          this.quill.blur();
        }));

      this.subs.add(this.richTextService.format$
        .pipe(filter(cmd => cmd.blockId === this.block.id))
        .subscribe(cmd => {
          this.format(cmd.kind, cmd.value, cmd.selection);
        }));

      this.subs.add(this.richTextService.formatEnd$
        .pipe(filter(blockIds => blockIds.indexOf(this.block.id) >= 0))
        .subscribe(blockIds => {
          const r = (this.quill as any).selection.getRange();

          if (r != null && r[0] != null) {
            this.state = {...this.state, ...RichTextToolbarState.fromQuillFormat(this.quill.getFormat())};
          }
        }));

      this.subs.add(this.richTextService.undo$
        .pipe(filter(ids => ids.indexOf(this.block.id) >= 0))
        .subscribe(ids => {
          (this.quill as any).history.undo();
        }));

      this.subs.add(this.richTextService.redo$
        .pipe(filter(ids => ids.indexOf(this.block.id) >= 0))
        .subscribe(ids => {
          (this.quill as any).history.redo();
        }));

      this.subs.add(this.richTextService.clean$
        .pipe(filter(cmd => cmd.blockIds?.includes(this.block.id)))
        .subscribe(cmd => {
          this.clean(cmd.selection);
        }));

      this.subs.add(this.richTextService.setHeader$
        .pipe(filter(cmd => cmd.blockIds?.indexOf(this.block.id) >= 0))
        .subscribe(cmd => {
          this.setHeader(cmd.header);
        }));

      this.subs.add(this.richTextService.insertVariable$
        .pipe(filter(cmd => cmd.blockId === this.block.id))
        .subscribe(cmd => {
          this.insertVariable(cmd.name);
        }));

      this.subs.add(this.richTextService.hideToolbar$.subscribe(_ => this.toolbarDropdown?.hide()));
    }
  }

  ngAfterViewInit() {
    this.quill = new Quill(this.editor.nativeElement, {
      readOnly: !this.blocksConfig?.editable,
      scrollingContainer: this.toolbarScrollingContainer
    });

    this.quill.root.style.justifyContent = this.block.settings.verticalAlignment;

    this.subs.add(fromEvent(this.quill.root, 'focus').subscribe(_ => {
      this.focused.emit();
    }));

    this.subs.add(fromEvent(this.quill.root, 'blur').subscribe(_ => {
      this.blurred.emit();
    }));

    if (!this.blocksConfig?.editable && 'history' in this.quill) {
      (this.quill as any).history.ignoreChange = true;
    }

    instances.set(this.quill, this);
    RichTextComponent.instaceMap.set(this.block.id, this);

    this.quill.setContents(this.block.localizedDelta(this.currentContentLocale, this.originalContentLocale) || this.block.settings.delta);

    if (this.blocksConfig?.editable) {
      this.subs.add(fromEvent(this.quill, 'text-change').subscribe(x => this.update(x as any)));
    }

    // Just listening for selection changes is not enough for detecting
    // the current format. Like the toolbar module we must subscribe to
    // 'editor-change' and 'scroll-optimize'.
    if (this.blocksConfig?.editable) {
      this.subs.add(fromEvent(this.quill, 'editor-change')
        .subscribe(value => {
          const [type, ...args] = value;

          if (!this.destroyed && type === 'selection-change') {
            const [range, oldRange, source] = args;
            this.selectionChange(range as any as RangeStatic);
          }
        }));

      this.subs.add(fromEvent(this.quill, 'scroll-optimize').subscribe(() => {
        if (!this.destroyed && 'selection' in this.quill) {
          const [range] = (this.quill as any).selection.getRange(); // quill.getSelection triggers update
          this.selectionChange(range);
        }
      }));
    }

    this.subs.add(this.blockChangeDetectionService.changedBlockIds$
      .pipe(filter((blockId: number) => blockId === this.block.id), map(id => this.blockStore.getSync(id)))
      .subscribe((block: RichText) => {
        if (this.preventUpdate) {
          this.preventUpdate = false;
          return;
        }

        if (block.delta && !this.blocksConfig?.editable) {
          this.quill.updateContents(block.delta, 'silent');
          delete block.delta;
        } else {
          const delta = new Delta(block.localizedDelta(this.currentContentLocale, this.originalContentLocale) || block.settings.delta);

          // FAST TRACK for large deltas because quill (Delta.diff) is slow
          const inEditMode = this.blocksConfig?.editable && this.block?.id === this.editorRoutingService?.selectedBlock;
          if (!inEditMode && (JSON.stringify(delta).length > 1000 || JSON.stringify(this.quill.getContents()).length > 1000)) {
            this.quill.setText('', 'silent');
            this.quill.setContents(delta, 'silent');
            return;
          }

          if (this.quill.getContents().diff(delta).ops.length === 0) {
            return;
          }

          this.quill.setContents(delta, 'silent');
        }
      }));

    if (this.blocksConfig?.editable) {
      this.subs.add(combineLatest([
        fromEvent(document, 'mousemove'),
        this.routerStore.isModalActive
      ]).pipe().subscribe(([mouseMoveEvent, modalActive]) => {
        // when there is no modal opened OR this RichText is within a modal
        // to not set typing to true => do NOT hide Toolbar
        if (modalActive && !this.modalComponent) {
          this.typing = true;
        } else {
          if (!this.typing || !this.quill.hasFocus()) {
            return;
          }
          this.typing = false;
        }
      }));
    }

    if (this.blocksConfig?.editable && this.block?.id === this.editorRoutingService?.selectedBlock) {
      this.quill.focus();
      this.typing = false;
    }
  }

  ngOnDestroy() {
    RichTextComponent.instaceMap.delete(this.block.id);
    this.destroyed = true;
    this.subs.unsubscribe();

    // Clear content to ensure variables are detached.
    this.quill.setText('', 'silent');
  }

  handleKeyDown(event: KeyboardEvent) {
    this.openVariableDropdown(event.key);
  }

  handleKeyUp(event: KeyboardEvent) {
    if (event.key.startsWith('Arrow')) {
      // If there was no previousSelection, the user just clicked into the editor.
      // If there was already one, we check if the user is at the beginning/end with the cursor.
      // If this is the case and we are in a table, we pass the keystroke to the table.
      if ((this.tableMode || this.externalToolbarTarget) && this.previousSelection && this.currentSelection
          && this.previousSelection?.length === this.currentSelection?.length && this.previousSelection?.index
          === this.currentSelection?.index) {
        this.tableComponent.handleRteKeyUp(event);
      } else {
        event.stopPropagation();
      }

      this.previousSelection = this.currentSelection;
    }
  }

  private update = ([delta, oldContents, source]) => {
    if (this.destroyed) {
      return;
    }

    if (!this.typing && delta.ops.some(op => op.insert || op.delete)) {
      this.typing = true;
    }

    // Prevent update coming from the same instance.
    this.preventUpdate = true;

    this.block.setLocalizedContent(this.currentContentLocale, this.originalContentLocale, this.quill.root.innerHTML);
    this.block.setLocalizedDelta(this.currentContentLocale, this.originalContentLocale, this.quill.getContents());
    this.block.delta = delta;

    this.subs.add(this.blockMutationService.updateBlock(this.block).subscribe());

    this.cdr.markForCheck();
  };

  format(kind: string, value?: any, selection?: RangeStatic) {
    if (selection) {
      this.quill.setSelection(selection);
    }

    this.quill.format(kind, value, 'user');
    this.richTextService.formatEnd$.next([this.block.id]);
  }

  setHeader(header: DropdownItem) {
    this.state.header = header.value && header;

    this.quill.format('header', header.value, 'user');

    if (header.value) {
      const {index} = this.quill.getSelection();
      const [blot, offset] = this.quill.getLine(index);

      this.state.size = undefined;
      this.state.bold = false;

      this.quill.formatText(index - offset, blot.length(), {
        size: undefined,
        bold: false
      });
    }
  }

  openVariableDropdown(key: string) {
    if (key !== '[') {
      return;
    }

    this.typing = false;

    this.richTextToolbarComponent.variableDropdownComponent.toggle();
    this.cdr.markForCheck();
    this.richTextToolbarComponent.variableSearchComponent?.inputRef.nativeElement.focus();

    return false;
  }

  insertVariable(name: string) {
    const range = this.quill.getSelection(true);

    this.quill.deleteText(range.index, range.length);
    this.quill.insertEmbed(range.index, 'variable', name, 'user');
    this.quill.setSelection(range.index + 1, 0);
  }

  clean(selection?: RangeStatic) {
    if (selection) {
      this.quill.setSelection(selection);
    }

    const range = this.quill.getSelection();
    if (!range) {
      return;
    }

    if (range.length > 0) {
      this.quill.removeFormat(range.index, range.length, 'user');
      return;
    }

    const formats = this.quill.getFormat();
    Object.keys(formats).forEach(name => {
      // Clean functionality in existing apps only clean inline formats
      if ('query' in this.quill.scroll && (this.quill.scroll as any).query(name, Parchment.Scope.INLINE) !== null) {
        this.quill.format(name, false, 'user');
      }
    });
  }

  private selectionChange = (range: RangeStatic) => {
    this.currentSelection = range;

    if (this.previousSelection == null) {
      this.previousSelection = this.currentSelection;
    }

    if (!range || this.destroyed) {
      return;
    }

    this.typing = false;

    const selectedText = this.quill.getText(range.index, range.length);

    this.selectedText = Token.ELIGIBLE_REGEX.test(selectedText) ? selectedText : TokenHelper.transformToEligibleToken(selectedText);
    this.textSelected.emit(this.selectedText);

    this.state = {...this.state, ...RichTextToolbarState.fromQuillFormat(this.quill.getFormat())};

    this.cdr.markForCheck();
  };
}
